import React, { useState } from "react";
import { Tag } from "rsuite";
import BasicPanel from "../../components/panels/basicPanel/basicPanel";
import "./packageTracking.css"
import { useTranslation } from "react-i18next";
import { useMessenger } from "../../context/messengerContext";
import { buildUserIdPayload } from "../../utils/userIdPayload";
import { getCdekStatusKey, getCdekCityKey } from "../../utils/cdekStatusKey";

const ARROW_SVG = 
<svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12.8333 8.125L17 12.5M17 12.5L12.8333 16.875M17 12.5L7 12.5" stroke="#B4B4B4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>




const LabelWithSubLabel = ({ label, subLabel }) => {
    return (
        <div className={`labelWithSubLabel`}>
            <div className="paragraph-12R" style={{color: "var(--Gray-700)"}}>{label}</div>
            <div className="paragraph-12R">{subLabel}</div>
        </div>
    )
  }
  
const LabelWithValueAndKey = ({ label, subLabel, value }) => {
    return (
        <div className={`labelWithValueAndKey`}>
            <LabelWithSubLabel label={label} subLabel={subLabel} />
            <div className="paragraph-12R labelValue" style={{color: "var(--Gray-900)"}}>{value}</div>
        </div>
    )
  }

export const getStatusEvents = (trackData) => {
    const events = [trackData?.statuses, trackData?.status_events, trackData?.events]
        .find((candidate) => Array.isArray(candidate) && candidate.length > 0);
    return Array.isArray(events) ? events.slice(0, 5) : [];
};

export const getSavedPackages = (trackData) => {
    const packages = trackData?.packages || trackData?.package_data || trackData?.package;
    if (Array.isArray(packages)) return packages;
    return packages && typeof packages === "object" ? [packages] : [];
};

export const formatEventDate = (value) => {
    if (!value) return "—";
    const russianDate = String(value).match(/^(\d{2})\.(\d{2})\.(\d{4})(?:\s+(\d{2}:\d{2}))?/);
    if (russianDate) return `${russianDate[1]}.${russianDate[2]}.${russianDate[3]}${russianDate[4] ? ` ${russianDate[4]}` : ""}`;
    const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:T| )(\d{2}):(\d{2})/);
    if (match) return `${match[3]}.${match[2]}.${match[1]} ${match[4]}:${match[5]}`;
    const dateOnly = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) return `${dateOnly[3]}.${dateOnly[2]}.${dateOnly[1]}`;
    return String(value);
};

export const formatPackageDimensions = (packageData) => {
    const { length, width, height } = packageData || {};
    if ([length, width, height].every((value) => value !== undefined && value !== null)) {
        return `${length} × ${width} × ${height} см`;
    }
    return null;
};

export default function TrackItem ({track_data}) {
    const [loadingPDF, setLoadingPDF] = useState(false);
    const [loadingBarcode, setLoadingBarcode] = useState(false);

    const { t } = useTranslation('basic');
    const { userId, platform } = useMessenger();

    // track_data.name comes from /get_package_info (CDEK API, always Russian).
    // track_data.status comes from /get_packages (DB-stored, also Russian from CDEK).
    const rawStatus = track_data.name || track_data.status || "";
    const statusKey = getCdekStatusKey(rawStatus);
    const displayStatus = statusKey ? t(statusKey) : rawStatus || "—";

    const cityKey = getCdekCityKey(track_data.current_city);
    const displayCity = cityKey ? t(cityKey) : track_data.current_city;
    const statusEvents = getStatusEvents(track_data);
    const savedPackages = getSavedPackages(track_data);
    const fromLocation = track_data.from_location || track_data.city_sender || "—";
    const toLocation = track_data.to_location || track_data.city_recipient || "—";

    const sendWaybill = async () => {
        try {
          setLoadingPDF(true);
          await fetch("/api/tracking/send/waybill", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ track_number: track_data.track_number, contract_number: track_data.contract_number, ...buildUserIdPayload(platform, userId) }),
          });
        } catch (err) {
          console.error("Ошибка при отправке PDF:", err);
        } finally {
          setLoadingPDF(false);
        }
      };

      const sendBarcode = async () => {
        try {
          setLoadingBarcode(true);
          await fetch("/api/tracking/send/barcode", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ track_number: track_data.track_number, contract_number: track_data.contract_number, ...buildUserIdPayload(platform, userId) }),
          });
        } catch (err) {
          console.error("Ошибка при отправке штрих-кода:", err);
        } finally {
          setLoadingBarcode(false);
        }
      };
    
    return (
        <BasicPanel>
            <div className={"trackItemInfo"}>
                <div className="packageInfoHeader">
                    <div className="packageInfoHeaderLeft">
                        <div className="paragraph-14M">{t("track_number_prefix")}{track_data.track_number}</div>
                        <div className="paragraph-10R">{t("contract_word")} {track_data.contract_number}</div>
                    </div>
                    <div className="packageInfoHeaderRight">
                        <Tag style={{color: "var(--primary)"}}>{displayStatus}</Tag>
                    </div>
                </div>


                <div className="trackItemButtons">
                    <button
                        className="track-btn"
                        onClick={sendWaybill}
                        disabled={loadingPDF}
                    >
                        {loadingPDF ? t("sending_progress") : "PDF"}
                    </button>
                    <button
                        className="track-btn"
                        onClick={sendBarcode}
                        disabled={loadingBarcode}
                    >
                        {loadingBarcode ? t("sending_progress") : t("barcode_button")}
                    </button>
                </div>

                <LabelWithValueAndKey label={t("current_location")} value={displayCity} />
                <LabelWithValueAndKey
                    label={t("planned_delivery_date")}
                    value={formatEventDate(track_data.planned_delivery_date)}
                    />
                <div className="trackItemCities">
                    <div className="trackItemCity paragraph-14R">
                        <span>{fromLocation}</span>
                        {track_data.company_name_sender ?
                            <span className="paragraph-12R" style={{color: "var(--Gray-800)"}}>{track_data.company_name_sender}</span>
                            :
                            null
                        }
                    </div>
                    <span className="aligned" style={{
                        width: "1.5rem",
                        height: "1.5rem",
                    }}>
                        {
                            ARROW_SVG
                        }
                    </span>
                    <div className="trackItemCity paragraph-14R">
                    <span>{toLocation}</span>
                        {track_data.company_name_recipient ?
                            <span className="paragraph-12R" style={{color: "var(--Gray-800)"}}>{track_data.company_name_recipient}</span>
                            :
                            null
                        }
                    </div>
                </div>

                {statusEvents.length > 0 && (
                    <div className="trackItemStatuses">
                        <div className="paragraph-14M">{t("tracking_status_history")}</div>
                        {statusEvents.map((event, index) => {
                            const eventStatus = event?.name || event?.status || event?.code || "";
                            const eventStatusKey = getCdekStatusKey(eventStatus);
                            const eventCityKey = getCdekCityKey(event?.city);
                            return (
                                <div className="trackItemStatus" key={`${event?.date_time || event?.date || eventStatus}-${index}`}>
                                    <div className="paragraph-12R trackItemStatusName">
                                        {eventStatusKey ? t(eventStatusKey) : eventStatus || "—"}
                                    </div>
                                    <div className="paragraph-12R trackItemStatusMeta">
                                        {eventCityKey ? t(eventCityKey) : event?.city || "—"}
                                        <span>{formatEventDate(event?.date_time || event?.date)}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {savedPackages.length > 0 && (
                    <div className="trackItemPackages">
                        <div className="paragraph-14M">{t("saved_package_data")}</div>
                        {savedPackages.map((packageData, index) => {
                            const dimensions = formatPackageDimensions(packageData);
                            const items = Array.isArray(packageData?.items) ? packageData.items : [];
                            return (
                                <div className="trackItemPackage" key={`${packageData?.weight_grams || packageData?.weight || "package"}-${index}`}>
                                    <div className="paragraph-12R">
                                        {t("package_number", { number: index + 1 })}
                                        {packageData?.weight_kg !== undefined && packageData?.weight_kg !== null
                                            ? ` · ${packageData.weight_kg} кг`
                                            : packageData?.weight_grams
                                                ? ` · ${packageData.weight_grams / 1000} кг`
                                                : packageData?.weight
                                                    ? ` · ${packageData.weight} кг`
                                                    : ""}
                                        {dimensions ? ` · ${dimensions}` : ""}
                                    </div>
                                    {items.length > 0 && (
                                        <div className="paragraph-12R trackItemPackageItems">
                                        {items.map((item) => `${item?.name || t("package_item")} × ${item?.amount || 1}`).join(", ")}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </BasicPanel>
    )
}
